/**
 * About Hero – component-based controller for sections/about-hero.liquid.
 *
 * Mirrors the original module's behavior:
 *   - Smooth-scrolls past the hero when the explore button is clicked.
 *   - Swaps the picture's `alt` between data-alt-mobile / data-alt-desktop
 *     based on viewport width (responsiveAltText utility).
 *
 * niceScroll → native window.scrollTo({ behavior: 'smooth' })
 * Scroll target: next .shopify-section top minus measured sticky top chrome.
 */

const SELECTOR_EXPLORE = '.js-aboutExplore';
const SELECTOR_RESPONSIVE_IMG = 'img[data-alt-mobile][data-alt-desktop]';
const MOBILE_BREAKPOINT = 1024;

/** Extra px to scroll past flush point — increase if a hero sliver remains below sticky chrome. */
const SCROLL_OVER_CLEAR_PX = 2;

function parseCssPx(value) {
  const parsed = parseFloat(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

/**
 * Next main-flow section after the hero wrapper (template-agnostic).
 * Skips hidden / zero-height siblings; see QA note on lazy zero-height sections.
 */
function getNextSection(el) {
  const heroSection = el.closest('.shopify-section');
  if (!heroSection) return null;

  let sibling = heroSection.nextElementSibling;
  while (sibling) {
    if (!sibling.classList.contains('shopify-section')) {
      sibling = sibling.nextElementSibling;
      continue;
    }
    const style = getComputedStyle(sibling);
    if (style.display === 'none' || style.visibility === 'hidden') {
      sibling = sibling.nextElementSibling;
      continue;
    }
    if (sibling.getBoundingClientRect().height > 0) return sibling;
    sibling = sibling.nextElementSibling;
  }
  return null;
}

/**
 * Height of sticky/fixed chrome that will overlap the viewport top after scroll.
 * Uses live layout — not --announcement-bar-height (promo scrolls away; only header sticks).
 */
function measureStickyTopChrome() {
  let pinnedBottom = 0;
  const seen = new Set();

  const candidates = document.querySelectorAll(
    '#shopify-section-group-header-group .shopify-section, ' +
      '#shopify-section-header, ' +
      '[id^="shopify-section-"][id*="header"], ' +
      '[id^="shopify-section-"][id*="announcement"]',
  );

  candidates.forEach((el) => {
    if (seen.has(el)) return;
    seen.add(el);

    const style = getComputedStyle(el);
    if (style.position !== 'sticky' && style.position !== 'fixed') return;

    const rect = el.getBoundingClientRect();
    if (rect.height <= 0 || rect.bottom <= 0) return;

    // Already pinned to the viewport top (e.g. header stuck after scroll).
    if (rect.top <= 1) {
      pinnedBottom = Math.max(pinnedBottom, rect.bottom);
    }
  });

  if (pinnedBottom > 0) {
    return { total: pinnedBottom, headerSection: pinnedBottom, promo: 0, pinned: pinnedBottom };
  }

  // At scroll 0 the sticky header usually sits below the promo — use header section height only.
  const headerSection = document.querySelector(
    '#shopify-section-header, [id^="shopify-section-"][id$="__header"]',
  );
  const innerHeader = document.getElementById('main-header');

  const headerSectionH = headerSection
    ? headerSection.getBoundingClientRect().height
    : 0;
  const innerHeaderH = innerHeader
    ? innerHeader.getBoundingClientRect().height
    : 0;
  const headerH = Math.max(headerSectionH, innerHeaderH);

  const promoWrapper = document.querySelector('.announcement-wrapper');
  const promoSection = promoWrapper?.closest('.shopify-section');
  const promoStyle = promoSection ? getComputedStyle(promoSection) : null;
  const promoPinned =
    promoSection &&
    promoStyle &&
    (promoStyle.position === 'sticky' || promoStyle.position === 'fixed') &&
    promoSection.getBoundingClientRect().top <= 1
      ? promoSection.getBoundingClientRect().bottom
      : 0;

  const cssFallback =
    parseCssPx(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) ||
    70;
  const total = Math.max(headerH + promoPinned, cssFallback);

  return {
    total,
    headerSection: headerH || cssFallback,
    promo: promoPinned,
    pinned: 0,
  };
}

class AboutHero extends HTMLElement {
  constructor() {
    super();
    this._onExploreClick = this._onExploreClick.bind(this);
    this._onResize = this._onResize.bind(this);
    this._resizeRaf = null;
  }

  connectedCallback() {
    this._bindEvents();
    this._updateResponsiveAltText();
  }

  disconnectedCallback() {
    this._unbindEvents();
  }

  /* --------------------------------------------------------------------- */
  /* Explore – smooth scroll past the hero                                 */
  /* --------------------------------------------------------------------- */

  _onExploreClick() {
    const nextSection = getNextSection(this);
    if (!nextSection) return;

    const scrollY = window.scrollY;
    const heroRect = this.getBoundingClientRect();
    const nextRect = nextSection.getBoundingClientRect();
    const chrome = measureStickyTopChrome();
    const stickyOffset = chrome.total;
    const nextTopDoc = nextRect.top + scrollY;
    const heroBottomDoc = heroRect.bottom + scrollY;
    const targetY = nextTopDoc - stickyOffset + SCROLL_OVER_CLEAR_PX;

    // eslint-disable-next-line no-console -- QA diagnostic for EXPLORE scroll offset tuning
    console.info('[about-hero:explore]', {
      nextSectionId: nextSection.id,
      nextSectionTop: nextRect.top,
      nextTopDoc,
      heroBottomDoc,
      topDelta: nextTopDoc - heroBottomDoc,
      stickyOffset,
      chromeBreakdown: chrome,
      scrollOverClearPx: SCROLL_OVER_CLEAR_PX,
      targetY,
      scrollYBefore: scrollY,
    });

    window.scrollTo({ top: Math.max(0, targetY), behavior: 'smooth' });
  }

  /* --------------------------------------------------------------------- */
  /* Responsive alt text                                                   */
  /* --------------------------------------------------------------------- */

  _updateResponsiveAltText() {
    const isMobile = window.innerWidth < MOBILE_BREAKPOINT;
    this.querySelectorAll(SELECTOR_RESPONSIVE_IMG).forEach((img) => {
      const alt = isMobile
        ? img.dataset.altMobile
        : img.dataset.altDesktop;
      if (typeof alt === 'string') img.setAttribute('alt', alt);
    });
  }

  _onResize() {
    if (this._resizeRaf) return;
    this._resizeRaf = window.requestAnimationFrame(() => {
      this._resizeRaf = null;
      this._updateResponsiveAltText();
    });
  }

  /* --------------------------------------------------------------------- */
  /* Event wiring                                                          */
  /* --------------------------------------------------------------------- */

  _bindEvents() {
    this.querySelectorAll(SELECTOR_EXPLORE).forEach((el) => {
      el.addEventListener('click', this._onExploreClick);
    });
    window.addEventListener('resize', this._onResize);
  }

  _unbindEvents() {
    this.querySelectorAll(SELECTOR_EXPLORE).forEach((el) => {
      el.removeEventListener('click', this._onExploreClick);
    });
    window.removeEventListener('resize', this._onResize);
    if (this._resizeRaf) {
      window.cancelAnimationFrame(this._resizeRaf);
      this._resizeRaf = null;
    }
  }
}

if (!customElements.get('about-hero')) {
  customElements.define('about-hero', AboutHero);
}
